import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { downloadFile } from '@utils/files';
import { captureFrames, HERO_WIDTH, MOMENT_WIDTH } from './captureFrames';
import { marksAt } from './frameMarks';
import { reportFrames } from './reportData';

/**
 * The PDF report of the results page as it is at the click (design: .task/browser-report/plan.html). The frames come
 * from a hidden copy of the player's video while the PDF code and its fonts load (import() on the click only); then
 * the PDF is made and saved. One report at a time; leaving the page stops it and saves nothing.
 *
 * download({ snapshot, video }): snapshot as reportData.buildReport takes it; video: { src (the player's URL),
 * renewSrc (a new signed URL), drawIndex (the tracks the player draws), kindColors }.
 */
const useReportPdf = ({ onError }) => {
	const [busy, setBusy] = useState(false);
	const run = useRef(null);
	useEffect(() => () => run.current?.abort(), []);

	const download = async ({ snapshot, video }) => {
		if (run.current) return;
		const controller = new AbortController();
		run.current = controller;
		setBusy(true);
		try {
			const { count } = snapshot;
			const { hero, moments } = reportFrames(count);
			const shots = [
				{ frame: hero, width: HERO_WIDTH },
				...moments.filter(f => f !== hero).map(frame => ({ frame, width: MOMENT_WIDTH })),
			];
			const line = count.mode === 'line' ? count.line : null;
			const [{ makeReportPdf }, { images, failures }] = await Promise.all([
				// the PDF code and its fonts load while the frames are read
				import('./ReportDocument').then(async mod => {
					await mod.fontText();
					return mod;
				}),
				captureFrames({
					src: video.src,
					renewSrc: video.renewSrc,
					shots,
					fps: count.index.fps,
					marksFor: (frame, width) =>
						marksAt(video.drawIndex, frame, {
							width,
							kindColors: video.kindColors,
							line,
						}),
					signal: controller.signal,
				}),
			]);
			if (failures.length)
				Sentry.captureMessage(
					`Video report: frames not read (${failures.join('; ')})`,
					'warning'
				);
			if (controller.signal.aborted) return;
			const { report, blob } = await makeReportPdf({ snapshot, images });
			if (controller.signal.aborted) return;
			const url = URL.createObjectURL(blob);
			downloadFile({ url, title: report.fileName, fileExtension: 'pdf' });
			setTimeout(() => URL.revokeObjectURL(url), 10000);
		} catch (err) {
			if (err?.name === 'AbortError' || controller.signal.aborted) return;
			// the frame capture may still run (the PDF code did not load): stop it
			controller.abort();
			Sentry.captureException(err);
			onError?.(err);
		} finally {
			if (run.current === controller) run.current = null;
			setBusy(false);
		}
	};

	return { busy, download };
};

export default useReportPdf;
